import React, { useEffect, useMemo, useState } from 'react';
import {
  assignToggleStyle,
  boardPanelStyle,
  checkGridStyle,
  checkRowStyle,
  chipStyle,
  chipWrapStyle,
  dashboardGridStyle,
  emptyNoticeStyle,
  itemGroupCountStyle,
  itemGroupHeaderStyle,
  listScrollStyle,
  panelBodyStyle,
  panelHeaderStyle,
  panelSubStyle,
  panelTitleStyle,
  rowMetaStyle,
  rowTitleStyle,
  searchStyle,
  sectionLabelStyle,
  selectableRowStyle,
} from './dashboardStyles';
import { compareGroupedEmployee, getEmployeeCompanyGroup, groupEmployees } from './employeeGrouping';
import { useTenantMenuInfo } from '../adminlayout/tenantMenuInfo';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function matchesEmployee(row, keyword) {
  const q = normalize(keyword);
  if (!q) return true;
  return [row.name, row.loginId, row.department, row.employeeNo, row.companyGroup?.label, row.companyGroup?.code]
    .some((value) => normalize(value).includes(q));
}

function AssignmentToggle({ assigned, disabled, onClick }) {
  return (
    <button type="button" style={assignToggleStyle(assigned)} disabled={disabled} onClick={onClick}>
      {assigned ? '배정됨' : '배정'}
    </button>
  );
}

export default function EmployeePermissionView({
  overview,
  isMobile,
  savingUserId,
  onGrant,
  onRevoke,
}) {
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  // 대표 회사 직원 묶음 머리글 = 회사 이름('PINE'·'LINK' 대신 '파인그룹'·'미팅링크', 2026-09-24). 모르면 종전.
  const { primaryCode, primaryName } = useTenantMenuInfo();

  const employees = useMemo(() => {
    const all = overview?.employeeSummaries || [];
    const hq = { code: primaryCode, name: primaryName };
    return all
      .map((row) => ({ ...row, companyGroup: getEmployeeCompanyGroup(row, hq) }))
      .sort(compareGroupedEmployee)
      .filter((row) => matchesEmployee(row, keyword));
  }, [overview, keyword, primaryCode, primaryName]);

  const employeeGroups = useMemo(() => groupEmployees(employees), [employees]);

  useEffect(() => {
    if (!selectedId && employees[0]) setSelectedId(employees[0].id);
    if (selectedId && employees.length && !employees.some((row) => row.id === selectedId)) {
      setSelectedId(employees[0].id);
    }
  }, [employees, selectedId]);

  const selected = employees.find((row) => row.id === selectedId) || employees[0] || null;
  const selectedTaskCodes = new Set((selected?.tasks || []).map((row) => row.code));
  const selectedFeatureIds = new Set((selected?.features || []).map((row) => Number(row.featureSetId)));

  if (!overview) return <div style={emptyNoticeStyle}>현황을 불러오는 중입니다.</div>;

  return (
    <div style={dashboardGridStyle(isMobile)}>
      <section style={boardPanelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>직원</h3>
          <span style={panelSubStyle}>{employees.length}명</span>
        </div>
        <div style={panelBodyStyle}>
          <input
            style={searchStyle}
            placeholder="직원명, 아이디, 부서, 사번 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div style={listScrollStyle}>
            {employees.length === 0 && <div style={emptyNoticeStyle}>검색 결과가 없습니다.</div>}
            {employeeGroups.map((group) => (
              <div key={group.key}>
                <div style={itemGroupHeaderStyle}>
                  <span>{group.label}</span>
                  <span style={itemGroupCountStyle}>{group.items.length}</span>
                </div>
                {group.items.map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    style={selectableRowStyle(emp.id === selected?.id)}
                    onClick={() => setSelectedId(emp.id)}
                  >
                    <div style={rowTitleStyle}>{emp.name}</div>
                    <div style={rowMetaStyle}>
                      {emp.employeeNo ? `사번 ${emp.employeeNo} · ` : ''}{emp.loginId} · {emp.department || '-'}
                    </div>
                    <div style={chipWrapStyle}>
                      <span style={chipStyle}>업무 {emp.taskCount}</span>
                      <span style={chipStyle}>기능 {emp.featureCount}</span>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={boardPanelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>{selected ? selected.name : '배정 상세'}</h3>
          {selected && <span style={panelSubStyle}>총 {selected.totalCount}개</span>}
        </div>
        <div style={panelBodyStyle}>
          {!selected ? (
            <div style={emptyNoticeStyle}>직원을 선택해 주세요.</div>
          ) : (
            <>
              <div style={sectionLabelStyle}>
                <span>업무 권한</span>
                <span>{selected.taskCount}개</span>
              </div>
              <div style={checkGridStyle}>
                {(overview.taskOptions || []).map((task) => {
                  const assigned = selectedTaskCodes.has(task.code);
                  const spec = { kind: 'task', taskCode: task.code, userId: selected.id };
                  return (
                    <div key={task.code} style={checkRowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <div style={rowTitleStyle}>{task.label}</div>
                        <div style={rowMetaStyle}>{task.code}</div>
                      </div>
                      <AssignmentToggle
                        assigned={assigned}
                        disabled={savingUserId === selected.id}
                        onClick={() => (assigned ? onRevoke(spec) : onGrant(spec))}
                      />
                    </div>
                  );
                })}
              </div>

              <div style={sectionLabelStyle}>
                <span>기능 권한</span>
                <span>{selected.featureCount}개</span>
              </div>
              <div style={checkGridStyle}>
                {(overview.featureSets || []).map((feature) => {
                  const assigned = selectedFeatureIds.has(Number(feature.id));
                  const spec = { kind: 'feature', featureSetId: feature.id, userId: selected.id };
                  return (
                    <div key={feature.id} style={checkRowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <div style={rowTitleStyle}>{feature.name}</div>
                        <div style={rowMetaStyle}>{feature.description || '-'}</div>
                      </div>
                      <AssignmentToggle
                        assigned={assigned}
                        disabled={savingUserId === selected.id}
                        onClick={() => (assigned ? onRevoke(spec) : onGrant(spec))}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
